// Color palette for the application
const baseColors = {
  // Primary forest green - used for forest areas and primary actions
  forest: {
    primary: '#1f4529',
    hex: 0x1f4529,
    name: 'Forest Green'
  },
  
  // Seed/cream color - used for seed gathering areas
  seed: {
    primary: '#f2ecd2',
    hex: 0xf2ecd2,
    name: 'Seed Cream'
  },
  
  // Background green - used for map background and secondary elements
  background: {
    primary: '#719f6f',
    hex: 0x719f6f,
    name: 'Background Green'
  },
  
  // Main background color - copy of primary forest color
  mainBackground: {
    primary: '#f2ecd2',
    hex: 0xf2ecd2,
    name: 'Main Background'
  },
  
  // Additional semantic colors
  semantic: {
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6'
  }
}

// Convert hex string to hex number
function hexStringToNumber(hexString: string): number {
  // Remove # if present
  const cleanHex = hexString.replace('#', '')
  const result = parseInt(cleanHex, 16)
  
  // Debug logging
  if (isNaN(result)) {
    console.warn(`⚠️ Invalid hex color: ${hexString}`)
    return 0x000000 // fallback to black
  }
  
  return result
}

// Apply color overrides
export function createColorsWithOverrides(overrides: {
  forest?: string
  seed?: string
  background?: string
  mainBackground?: string
} = {}) {
  const colors = { ...baseColors }
  
  if (overrides.forest) {
    colors.forest = {
      primary: overrides.forest,
      hex: hexStringToNumber(overrides.forest),
      name: 'Custom Forest'
    } as typeof colors.forest
  }
  
  if (overrides.seed) {
    colors.seed = {
      primary: overrides.seed,
      hex: hexStringToNumber(overrides.seed),
      name: 'Custom Seed'
    } as typeof colors.seed
  }
  
  if (overrides.background) {
    colors.background = {
      primary: overrides.background,
      hex: hexStringToNumber(overrides.background),
      name: 'Custom Background'
    } as typeof colors.background
  }
  
  if (overrides.mainBackground) {
    colors.mainBackground = {
      primary: overrides.mainBackground,
      hex: hexStringToNumber(overrides.mainBackground),
      name: 'Custom Main Background'
    } as typeof colors.mainBackground
  }
  
  return colors
}

// Default colors (for backward compatibility)
export const colors = baseColors

// Helper function to get hex color for amCharts
export const getAmChartsColor = (colorKey: 'forest' | 'seed' | 'background' | 'mainBackground', customColors?: ReturnType<typeof createColorsWithOverrides>) => {
  const colorSet = customColors || colors
  return colorSet[colorKey].hex
}

// Helper function to get CSS color
export const getCssColor = (colorKey: 'forest' | 'seed' | 'background' | 'mainBackground', customColors?: ReturnType<typeof createColorsWithOverrides>) => {
  const colorSet = customColors || colors
  return colorSet[colorKey].primary
}

// Type definitions for better TypeScript support
export type ColorKey = keyof typeof colors
export type ForestColorKey = keyof typeof colors.forest
export type SeedColorKey = keyof typeof colors.seed
export type BackgroundColorKey = keyof typeof colors.background
export type MainBackgroundColorKey = keyof typeof colors.mainBackground
export type SemanticColorKey = keyof typeof colors.semantic
